import { basePathRewrite } from '@components/common';
import { Regex } from '@components/common/constant';
import { GoogleRecaptcha } from '@components/common/googleRecaptcha/googleRecaptcha';
import Toaster from '@components/common/toaster';
import {
  BtnWrap,
  LoginFormWrap,
  LoginWrap,
  PageDesc,
  PageHeading,
  PageWrap,
} from '@components/loginUpdated/style';
import { IDeviceType } from '@components/shared.types';
import { Button, FormGroup } from '@license-admin/boldjobs';
import { getUserByEmail } from 'API/ApiHelpers';
import { getENVPath, isPortalContent } from 'helper';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

interface Props {
  localization: any;
  config: any;
  deviceType: IDeviceType;
  isEmployerLogin: boolean;
}
type IFormData = {
  email: string;
  emailErr: string;
};

type IGoogleReCaptcha = {
  isValid: boolean;
  showError: boolean;
  errorMessage: string;
};

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    grecaptcha: {
      getResponse: () => string;
      reset: () => void;
    };
  }
}

export interface IRecaptchaHandle {
  validateRecaptcha: () => boolean;
}

function PasswordRecovery(props: Props) {
  const { localization, config } = props;
  const router = useRouter();
  const [formData, setFormData] = useState<IFormData>({
    email: '',
    emailErr: '',
  });
  const [googleCaptcha, setGoogleCaptcha] = useState<IGoogleReCaptcha>({
    isValid: true,
    showError: false,
    errorMessage: '',
  });
  const [captchaError, setCaptchaError] = useState<string>('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Captcha states and refs
  //const [showCaptcha, setShowCaptcha] = useState(false);

  // Check session storage on mount
  // not initialized in the constructor to avoid hydration issues
  // useEffect(() => {
  //   if (typeof window !== 'undefined') {
  //     const stored = sessionStorageGetItem(_CONSTANTS.FRGT_PWD_CAPTCHA_COUNT);
  //     if (stored && parseInt(stored, 10) > captchaMinCount) {
  //       setShowCaptcha(true);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const loadRecaptchaScript = () => {
      if (typeof window !== 'undefined' && !window.grecaptcha) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    };

    loadRecaptchaScript();
  }, []);

  const validateRecaptcha = (): boolean => {
    if (window.grecaptcha) {
      const captchaResponse = window.grecaptcha.getResponse();
      const isValid = Boolean(captchaResponse && captchaResponse.length > 0);
      if (!isValid) {
        setGoogleCaptcha({
          isValid: false,
          showError: true,
          errorMessage: 'Please validate the captcha to login',
        });
      }
      return isValid;
    }
    return true;
  };

  const handleRecaptchaChange = async (token: string | null) => {
    setCaptchaToken(token);
    const errorMessage = token
      ? ''
      : localization.please_Validate_Captcha_To_Login ||
        'Please validate the captcha to continue';
    setCaptchaError(errorMessage);
    setGoogleCaptcha({
      isValid: !!token,
      showError: !token,
      errorMessage,
    });
  };

  const handleEmailChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = ev.target;
    const newFormData: IFormData = { ...formData };
    newFormData.email = value;
    newFormData.emailErr = '';
    setFormData(newFormData);
  };

  const validateForm = async (): Promise<boolean> => {
    let isValid = true;

    const newFormData = { ...formData };
    if (formData.email.trim() === '') {
      newFormData.emailErr = 'Invalid email: You must provide an email';
      isValid = false;
    }
    if (formData.email.trim() && !Regex.email.test(formData.email.trim())) {
      isValid = false;
      newFormData.emailErr = 'Invalid email';
    }
    if (isValid) {
      const isUserExist = await getUserByEmail(formData.email, config.portalCD);
      if (!isUserExist.data) {
        isValid = false;
        newFormData.emailErr = `The email address you entered was not found in our records. Please verify that it is the email address you used to sign up with  ${isPortalContent}.`;
      }
    }
    setFormData(newFormData);
    return isValid;
  };

  const handleSubmit = async (ev: React.SyntheticEvent): Promise<void> => {
    ev.preventDefault();

    if (!validateRecaptcha()) {
      return;
    }

    // form validation as first step
    const isFormValid = await validateForm();
    if (!isFormValid) {
      // If form is not valid, do not proceed
      return;
    }

    // // captcha verification as second step
    // if (typeof window !== 'undefined') {
    //   let stored = sessionStorageGetItem(_CONSTANTS.FRGT_PWD_CAPTCHA_COUNT);
    //   let count = stored ? parseInt(stored, 10) : 0;
    //   count += 1;
    //   sessionStorage.setItem(
    //     _CONSTANTS.FRGT_PWD_CAPTCHA_COUNT,
    //     count.toString()
    //   );
    // }

    // If captcha is not required or already validated, proceed
    let redirectUrl = basePathRewrite('/settings?tab=3');
    await window.LOGIN.Accounts.forgotPassword(
      formData.email,
      config.productCD,
      '',
      '',
      false,
      redirectUrl,
      '',
      '',
      '',
      captchaToken || ''
    );
    router.push(
      { pathname: '/login', query: { forgotPassword: true } },
      '/login'
    );
  };

  return (
    <PageWrap>
      <LoginWrap>
        <PageHeading id="recovery-heading">
          <img
            src={`${
              getENVPath().NEXT_PUBLIC_CONTENT_URL
            }/images/icn-password-help.svg`}
            alt="Welcome Back icon"
            title="Welcome Back icon "
            width={44}
            height={44}
          />
          {localization.heading}
        </PageHeading>
        <PageDesc id="login-description">
          <span>{localization.sub_heading_one}</span>
          <span>{localization.sub_heading_two}</span>
        </PageDesc>
        <LoginFormWrap>
          <form onSubmit={handleSubmit}>
            <FormGroup
              type="text"
              value={formData.email}
              id="recovery-email"
              label={localization.email_address}
              autofocus={true}
              maxLength={50}
              updateFieldData={(ev: React.ChangeEvent<HTMLInputElement>) =>
                handleEmailChange(ev)
              }
            />
            {formData.emailErr !== '' && (
              <Toaster
                id="invalid-email-alert"
                description={`<strong>Oops!</strong> ${formData.emailErr}`}
                status="alert"
              ></Toaster>
            )}

            <GoogleRecaptcha
              onChange={handleRecaptchaChange}
              errorMessage={captchaError || googleCaptcha.errorMessage}
            />

            <BtnWrap>
              <Button
                id="send-email"
                buttonType="primary"
                text={localization.send_email}
                buttonSize={'default'}
                btnType="submit"
              />
            </BtnWrap>
          </form>
        </LoginFormWrap>
      </LoginWrap>
    </PageWrap>
  );
}

export default PasswordRecovery;
